// ActivityLogPage
// Global-admin view of the centralized portal activity (audit) log — "who did
// what" across every mutating endpoint. Backed by the PortalActivityLog Table
// Storage table via GET /api/manage/portal-activity (Write-PortalActivity writes
// one row per successful mutating action). v1.91.0.
//
// Design mirrors RecentEventsPage: date-range presets + free-text search drive
// the server query; per-column filters / sort / pagination are client-side;
// the timezone picker shares TZ_STORAGE_KEY with the Alerts + Coverage pages.

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ClipboardDocumentListIcon, UsersIcon } from '@heroicons/react/24/outline';
import { fetchPortalActivity } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import Pagination from './Pagination';
import { RefreshButton } from './ui/LoadingState';
import Badge from './ui/Badge';
import { activityCategoryTone } from './ui/badgeTones';
import {
  TIMEZONE_OPTIONS,
  TZ_STORAGE_KEY,
  getDefaultTimezone,
  getTzLabel,
  getTzOffset,
  resolveIana,
} from './TimelineView';
import {
  compareVals,
  applyColFilters,
  countActiveFilters,
  useResizableCols,
  SortIndicator,
  FilterCell,
} from './coverage/coverageShared';

// ── Static configuration ─────────────────────────────────────────────────────

// Day-bounded presets. The backend walks one Table Storage partition per calendar
// day, so we keep the largest preset at 90 days rather than an unbounded "All".
const RANGE_OPTIONS = [
  { key: '24h', label: '24h', days: 1 },
  { key: '3d',  label: '3d',  days: 3 },
  { key: '7d',  label: '7d',  days: 7 },
  { key: '30d', label: '30d', days: 30 },
  { key: '90d', label: '90d', days: 90 },
];

const COLS = [
  { key: 'performedUtc', label: 'Time',     w: 200, align: 'left', sort: true,  filterType: null },
  { key: 'actorUpn',     label: 'Actor',    w: 220, align: 'left', sort: true,  filterType: 'text' },
  { key: 'action',       label: 'Action',   w: 180, align: 'left', sort: true,  filterType: 'text' },
  { key: 'category',     label: 'Category', w: 130, align: 'left', sort: true,  filterType: 'multi-select' },
  { key: 'target',       label: 'Target',   w: 260, align: 'left', sort: true,  filterType: 'text' },
  { key: 'mspCode',      label: 'MSP',      w: 90,  align: 'left', sort: true,  filterType: 'text' },
  { key: 'summary',      label: 'Summary',  w: 380, align: 'left', sort: true,  filterType: 'text' },
];

const initFilters = () => ({
  actorUpn: '', action: '', category: '', target: '', mspCode: '', summary: '',
});

function formatTime(iso, tzIana) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  try {
    return d.toLocaleString('en-US', {
      timeZone: tzIana,
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    });
  } catch {
    return d.toISOString();
  }
}

// ── Component ────────────────────────────────────────────────────────────────

export default function ActivityLogPage() {
  const { user } = useAuth();
  const isGlobalAdmin = !!user?.isGlobalAdmin;

  // ── Toolbar state ──────────────────────────────────────────────────────────
  const [rangeKey, setRangeKey] = useState('7d');
  const [search, setSearch] = useState('');
  const [showTopUsers, setShowTopUsers] = useState(false);
  // The range actually loaded into `rows` (may differ from `rangeKey` until the
  // next Refresh) — used to label the Top Users panel honestly.
  const [loadedRangeKey, setLoadedRangeKey] = useState('7d');

  // ── Data state ─────────────────────────────────────────────────────────────
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastRefreshUtc, setLastRefreshUtc] = useState(null);
  const [loadTimeMs, setLoadTimeMs] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  // ── View state ─────────────────────────────────────────────────────────────
  const [selectedTz, setSelectedTz] = useState(getDefaultTimezone);
  useEffect(() => {
    try { localStorage.setItem(TZ_STORAGE_KEY, selectedTz); } catch { /* ignore */ }
  }, [selectedTz]);
  const tzObj = TIMEZONE_OPTIONS.find((t) => t.id === selectedTz) || TIMEZONE_OPTIONS[0];
  // 'Local' has iana '' which throws when passed straight to Intl/toLocaleString;
  // resolveIana() maps empty → the browser zone. See feedback-local-tz-empty-iana.
  const tzIana = resolveIana(tzObj.iana);

  const [expanded, setExpanded] = useState(() => new Set());
  const toggleExpand = useCallback((id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const [filters, setFilters] = useState(initFilters());
  const [sortField, setSortField] = useState('performedUtc');
  const [sortDir, setSortDir] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const setFilter = useCallback((key, value) => {
    setFilters((p) => ({ ...p, [key]: value }));
    setCurrentPage(1);
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(initFilters());
    setCurrentPage(1);
  }, []);

  const handleSort = useCallback((field) => {
    setSortField((prev) => {
      if (prev === field) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return field;
      }
      setSortDir('desc');
      return field;
    });
    setCurrentPage(1);
  }, []);

  // ── Refresh / fetch ──────────────────────────────────────────────────────
  const doRefresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    setExpanded(new Set());
    const start = performance.now();
    try {
      const range = RANGE_OPTIONS.find((r) => r.key === rangeKey) || RANGE_OPTIONS[2];
      const dateTo = new Date();
      const dateFrom = new Date(dateTo.getTime() - range.days * 24 * 60 * 60 * 1000);
      const data = await fetchPortalActivity({
        dateFrom: dateFrom.toISOString(),
        dateTo: dateTo.toISOString(),
        search: search || undefined,
        top: 2000,
      });
      // Derive a stable per-row id (the API doesn't return the RowKey) and a
      // combined Target display field for sorting / filtering.
      const mapped = (Array.isArray(data) ? data : []).map((r, i) => ({
        ...r,
        _id: i,
        target: r.targetName || r.targetId || '',
      }));
      setRows(mapped);
    } catch (err) {
      setError(err?.message || 'Failed to load the activity log.');
      setRows([]);
    } finally {
      setLoadTimeMs(performance.now() - start);
      setLastRefreshUtc(new Date());
      setLoadedRangeKey(rangeKey);
      setLoading(false);
      setHasLoaded(true);
    }
  }, [rangeKey, search]);

  // Auto-load on mount (cheap, date-bucketed query) so admins see recent
  // activity immediately; the Refresh button re-runs with the current scope.
  useEffect(() => { doRefresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // ── Distinct values for the category multi-select filter ───────────────────
  const distinct = useMemo(() => ({
    category: [...new Set(rows.map((r) => r.category).filter(Boolean))].sort(),
  }), [rows]);

  // ── Top-users aggregation (global-admin only) ──────────────────────────────
  // Ranks users across the full fetched `rows` for the loaded range (not the
  // client-side column-filtered set) — "top users for the selected date range".
  const topUsers = useMemo(() => {
    const byUser = new Map();
    for (const r of rows) {
      const upn = r.actorUpn || '(unknown)';
      let u = byUser.get(upn);
      if (!u) {
        u = { upn, displayName: r.actorDisplayName || '', total: 0, actions: new Map(), categories: new Map() };
        byUser.set(upn, u);
      }
      u.total += 1;
      if (r.action)   u.actions.set(r.action, (u.actions.get(r.action) || 0) + 1);
      if (r.category) u.categories.set(r.category, (u.categories.get(r.category) || 0) + 1);
    }
    const topOf = (m) => {
      let bestKey = null, bestVal = 0;
      for (const [k, v] of m) { if (v > bestVal) { bestKey = k; bestVal = v; } }
      return bestKey ? { key: bestKey, count: bestVal } : null;
    };
    return [...byUser.values()]
      .map((u) => ({
        upn: u.upn,
        displayName: u.displayName,
        total: u.total,
        topAction: topOf(u.actions),
        topCategory: topOf(u.categories),
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 20);
  }, [rows]);

  const loadedRangeLabel = (RANGE_OPTIONS.find((r) => r.key === loadedRangeKey) || RANGE_OPTIONS[2]).label;

  // ── Filter + sort pipeline ─────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let r = applyColFilters(rows, COLS, filters);
    if (sortField) {
      r = [...r].sort((a, b) => compareVals(a[sortField], b[sortField], sortDir));
    }
    return r;
  }, [rows, filters, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = useMemo(
    () => filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [filtered, currentPage, pageSize]
  );

  const activeFilterCount = countActiveFilters(filters);

  // ── Column-resize state ────────────────────────────────────────────────────
  const { colWidths, thRefs, tableRef, startResize } = useResizableCols(COLS.map((c) => c.w));
  const chevronColWidth = 32;
  const totalWidth = chevronColWidth + colWidths.reduce((a, b) => a + b, 0);

  // ── Load-status strip ──────────────────────────────────────────────────────
  const refreshStatus = (() => {
    if (loading) return <span className="text-blue-400">Loading…</span>;
    if (error)   return <span className="text-red-400">{error}</span>;
    if (loadTimeMs == null) return null;
    const rowsLabel = filtered.length === 1 ? '1 entry' : `${filtered.length.toLocaleString()} entries`;
    const totalLabel = filtered.length === rows.length
      ? rowsLabel
      : `${rowsLabel} (of ${rows.length.toLocaleString()})`;
    const seconds = (loadTimeMs / 1000).toFixed(1);
    const refreshedAt = lastRefreshUtc
      ? lastRefreshUtc.toLocaleTimeString('en-US', {
          timeZone: tzIana, hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
        })
      : '';
    return (
      <span>
        Loaded {totalLabel} in <span className="text-gray-300">{seconds}s</span>
        {refreshedAt && (
          <> · last refreshed <span className="text-gray-400">{refreshedAt} {getTzLabel(selectedTz)}</span></>
        )}
      </span>
    );
  })();

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="p-6 space-y-6">
      {/* Page header */}
      <div>
        <div className="text-xs text-gray-500 mb-1">Administration</div>
        <h1 className="text-xl font-semibold text-gray-100 flex items-center gap-2">
          <ClipboardDocumentListIcon className="h-5 w-5 text-blue-400" />
          Activity Log
        </h1>
        <p className="text-xs text-gray-500 mt-1">
          Portal-wide audit trail of every change made through the portal — tenant onboarding/edits,
          rule changes, allowlist edits, ticketing/MSP config, alert status changes, and job triggers.
          Sorting, filtering, and pagination are client-side.
        </p>
      </div>

      {/* Toolbar */}
      <div className="card p-4">
        <div className="flex flex-wrap items-center gap-3">
          {/* Date-range presets */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-400 whitespace-nowrap">Range</label>
            <div className="inline-flex rounded-md border border-gray-700 overflow-hidden">
              {RANGE_OPTIONS.map((r) => (
                <button
                  key={r.key}
                  type="button"
                  onClick={() => setRangeKey(r.key)}
                  className={`px-2.5 py-1 text-xs border-r border-gray-700 last:border-r-0 ${
                    rangeKey === r.key
                      ? 'bg-blue-600/30 text-blue-300'
                      : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          {/* Search */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-400 whitespace-nowrap">Search</label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') doRefresh(); }}
              placeholder="actor / target / summary / MSP"
              className="input-field py-1 px-2 text-xs min-w-[220px]"
            />
          </div>

          {/* Refresh */}
          <RefreshButton
            loading={loading}
            onClick={doRefresh}
            className="btn-primary text-xs px-3 py-1.5 flex items-center gap-1.5"
          />

          {/* Top Users toggle (global-admin only) */}
          {isGlobalAdmin && (
            <button
              type="button"
              onClick={() => setShowTopUsers((v) => !v)}
              className={`text-xs px-3 py-1.5 rounded-md border flex items-center gap-1.5 ${
                showTopUsers
                  ? 'bg-blue-600/30 text-blue-300 border-blue-500/50'
                  : 'text-gray-300 border-gray-700 hover:bg-gray-800 hover:text-gray-100'
              }`}
              aria-pressed={showTopUsers}
            >
              <UsersIcon className="h-4 w-4" />
              Top Users
            </button>
          )}

          {/* Load timer */}
          <div className="text-[11px] text-gray-500 flex-1 min-w-[8rem]">{refreshStatus}</div>

          {/* Timezone picker — shared with Alerts + Coverage pages */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-400 whitespace-nowrap">Time zone</label>
            <select
              value={selectedTz}
              onChange={(e) => setSelectedTz(e.target.value)}
              className="input-field py-1 px-2 text-xs"
            >
              {TIMEZONE_OPTIONS.map((tz) => (
                <option key={tz.id} value={tz.id}>{tz.label} ({getTzOffset(tz.iana)})</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Top Users panel (global-admin only) — on-page, toggled from the toolbar */}
      {isGlobalAdmin && showTopUsers && (
        <div className="card p-0 overflow-hidden">
          <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 border-b border-gray-800">
            <UsersIcon className="h-4 w-4 text-blue-400" />
            <span className="text-sm font-medium text-gray-200">Top users — last {loadedRangeLabel}</span>
            <span className="text-xs text-gray-500">
              {rows.length.toLocaleString()} {rows.length === 1 ? 'entry' : 'entries'} analyzed
            </span>
            {rows.length >= 2000 && (
              <span className="text-[11px] text-amber-400/90">
                Ranking is based on the 2000 most-recent entries in this range.
              </span>
            )}
            <button
              type="button"
              onClick={() => setShowTopUsers(false)}
              className="ml-auto text-xs text-gray-400 hover:text-gray-200 border border-gray-700 hover:border-gray-600 rounded px-2 py-0.5"
            >
              Hide
            </button>
          </div>
          {topUsers.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-gray-500">
              {loading ? 'Loading activity…' : 'No activity in this range to rank.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="text-xs text-gray-300 w-full">
                <thead>
                  <tr className="border-b border-gray-800 bg-gray-900/60 text-gray-400">
                    <th className="px-3 py-2 text-right font-medium w-12">#</th>
                    <th className="px-3 py-2 text-left font-medium">User</th>
                    <th className="px-3 py-2 text-right font-medium w-28">Total actions</th>
                    <th className="px-3 py-2 text-left font-medium">Most frequent action</th>
                    <th className="px-3 py-2 text-left font-medium">Top category</th>
                  </tr>
                </thead>
                <tbody>
                  {topUsers.map((u, i) => (
                    <tr key={u.upn} className="border-b border-gray-800/60 hover:bg-gray-800/40">
                      <td className="px-3 py-2 text-right text-gray-500 align-top">{i + 1}</td>
                      <td className="px-3 py-2 align-top">
                        <div className="text-gray-200 truncate max-w-[280px]" title={u.upn}>{u.upn}</div>
                        {u.displayName && (
                          <div className="text-[10px] text-gray-500 truncate max-w-[280px]" title={u.displayName}>{u.displayName}</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-200 font-medium align-top">{u.total.toLocaleString()}</td>
                      <td className="px-3 py-2 align-top">
                        {u.topAction ? (
                          <span className="font-mono text-[11px] text-gray-200">
                            {u.topAction.key} <span className="text-gray-500">×{u.topAction.count}</span>
                          </span>
                        ) : <span className="text-gray-600">—</span>}
                      </td>
                      <td className="px-3 py-2 align-top">
                        {u.topCategory ? (
                          <span className="inline-flex items-center gap-1.5">
                            <Badge tone={activityCategoryTone(u.topCategory.key)} dot>{u.topCategory.key}</Badge>
                            <span className="text-gray-500">×{u.topCategory.count}</span>
                          </span>
                        ) : <span className="text-gray-600">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Results table */}
      <div className="card overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 px-4 py-2.5 border-b border-gray-800 min-h-[40px]">
          <div className="flex-1 min-w-[8rem] text-xs text-gray-500">
            {filtered.length.toLocaleString()} {filtered.length === 1 ? 'entry' : 'entries'} after filters
          </div>
          {activeFilterCount > 0 && (
            <button
              type="button"
              onClick={clearFilters}
              className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-200 border border-gray-700 hover:border-gray-600 rounded px-2 py-0.5"
            >
              Clear {activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''}
            </button>
          )}
        </div>

        <div className="overflow-x-auto">
          <table ref={tableRef} className="text-xs text-gray-300" style={{ tableLayout: 'fixed', width: `${totalWidth}px` }}>
            <thead>
              <tr className="border-b border-gray-800 bg-gray-900/60">
                <th style={{ width: `${chevronColWidth}px` }} className="px-2 py-2"></th>
                {COLS.map((col, i) => (
                  <th
                    key={col.key}
                    ref={(el) => { thRefs.current[i] = el; }}
                    style={{ width: `${colWidths[i]}px` }}
                    className="px-3 py-2 font-medium text-gray-400 whitespace-nowrap relative select-none cursor-pointer text-left"
                    onClick={() => col.sort && handleSort(col.key)}
                  >
                    <span className="text-xs">
                      {col.key === 'performedUtc' ? `Time (${getTzLabel(selectedTz)})` : col.label}
                    </span>
                    <SortIndicator active={sortField === col.key} dir={sortDir} />
                    <div
                      className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-10 flex items-stretch justify-center group"
                      onMouseDown={(e) => { e.stopPropagation(); startResize(e, i); }}
                    >
                      <div className="w-px bg-gray-700/50 group-hover:bg-blue-500/70 transition-colors" />
                    </div>
                  </th>
                ))}
              </tr>
              <tr className="border-b border-gray-800 bg-gray-900/30">
                <td style={{ width: `${chevronColWidth}px` }} className="px-1.5 py-1"></td>
                {COLS.map((col, i) => (
                  <td key={col.key} style={{ width: `${colWidths[i]}px` }} className="px-1.5 py-1">
                    {col.filterType
                      ? <FilterCell col={col} filters={filters} onFilterChange={setFilter} distinct={distinct} />
                      : null}
                  </td>
                ))}
              </tr>
            </thead>
            <tbody>
              {paged.length === 0 ? (
                <tr>
                  <td colSpan={COLS.length + 1} className="px-3 py-8 text-center text-gray-500">
                    {loading
                      ? 'Loading activity…'
                      : !hasLoaded
                        ? 'Click Refresh to load activity.'
                        : rows.length === 0
                          ? 'No activity recorded in this window.'
                          : 'No entries match the current filters.'}
                  </td>
                </tr>
              ) : (
                paged.map((r) => (
                  <ActivityRow
                    key={r._id}
                    row={r}
                    expanded={expanded.has(r._id)}
                    onToggle={() => toggleExpand(r._id)}
                    tzIana={tzIana}
                    colWidths={colWidths}
                    chevronColWidth={chevronColWidth}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        {filtered.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            totalItems={filtered.length}
            onPageChange={setCurrentPage}
            onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1); }}
          />
        )}
      </div>
    </div>
  );
}

// ── Row + expanded body ────────────────────────────────────────────────────

function ActivityRow({ row, expanded, onToggle, tzIana, colWidths, chevronColWidth }) {
  const prettyDetails = useMemo(() => {
    if (!expanded || !row.detailsJson) return null;
    try { return JSON.stringify(JSON.parse(row.detailsJson), null, 2); }
    catch { return row.detailsJson; }
  }, [expanded, row.detailsJson]);

  return (
    <>
      <tr
        className="border-b border-gray-800/60 hover:bg-gray-800/40 cursor-pointer"
        onClick={onToggle}
      >
        <td style={{ width: `${chevronColWidth}px` }} className="px-2 py-2 text-gray-500 align-top">
          <span className="inline-block">{expanded ? '▾' : '▸'}</span>
        </td>
        <td style={{ width: `${colWidths[0]}px` }} className="px-3 py-2 align-top whitespace-nowrap text-gray-300">
          {formatTime(row.performedUtc, tzIana)}
        </td>
        <td style={{ width: `${colWidths[1]}px` }} className="px-3 py-2 align-top truncate" title={row.actorDisplayName || row.actorUpn}>
          {row.actorUpn || '—'}
        </td>
        <td style={{ width: `${colWidths[2]}px` }} className="px-3 py-2 align-top">
          <span className="font-mono text-[11px] text-gray-200">{row.action}</span>
        </td>
        <td style={{ width: `${colWidths[3]}px` }} className="px-3 py-2 align-top">
          {row.category ? <Badge tone={activityCategoryTone(row.category)} dot>{row.category}</Badge> : null}
        </td>
        <td style={{ width: `${colWidths[4]}px` }} className="px-3 py-2 align-top">
          <div className="truncate" title={row.target}>{row.target || '—'}</div>
          {row.targetType && (
            <div className="text-[10px] text-gray-500 truncate" title={row.targetType}>{row.targetType}</div>
          )}
        </td>
        <td style={{ width: `${colWidths[5]}px` }} className="px-3 py-2 align-top text-gray-400">
          {row.mspCode || '—'}
        </td>
        <td style={{ width: `${colWidths[6]}px` }} className="px-3 py-2 align-top text-gray-300">
          <div className="truncate" title={row.summary}>{row.summary || '—'}</div>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-gray-900/40 border-b border-gray-800/60">
          <td colSpan={COLS.length + 1} className="px-6 py-3">
            <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-[11px] text-gray-400 mb-3">
              <div><span className="text-gray-500">Actor:</span> <span className="text-gray-300">{row.actorDisplayName || row.actorUpn} ({row.actorUpn})</span></div>
              <div><span className="text-gray-500">When (UTC):</span> <span className="text-gray-300">{row.performedUtc}</span></div>
              <div><span className="text-gray-500">Target:</span> <span className="text-gray-300">{row.targetType ? `${row.targetType} · ` : ''}{row.targetId || '—'}</span></div>
              <div><span className="text-gray-500">Source IP:</span> <span className="text-gray-300">{row.sourceIp || '—'}</span></div>
              {row.mspCode && <div><span className="text-gray-500">MSP:</span> <span className="text-gray-300">{row.mspCode}</span></div>}
            </div>
            {prettyDetails ? (
              <div>
                <div className="text-[11px] text-gray-500 mb-1">Details</div>
                <pre className="text-[11px] text-gray-300 bg-gray-950/60 border border-gray-800 rounded p-3 overflow-x-auto whitespace-pre-wrap break-words">{prettyDetails}</pre>
              </div>
            ) : (
              <div className="text-[11px] text-gray-600 italic">No additional details recorded.</div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
